import { useState, useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { axiosInstance, API } from "../App";
import { toast } from "sonner";
import { ArrowLeft, FileText, TrendingUp, Download } from "lucide-react";

const Laporan = ({ user }) => {
  // Redirect non-admin users to dashboard
  if (user?.role !== 'admin') {
    return <Navigate to="/" replace />;
  }
  const [counters, setCounters] = useState([]);
  const [laporan, setLaporan] = useState([]);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  
  const currentDate = new Date();
  const [filters, setFilters] = useState({
    tahun: currentDate.getFullYear(),
    bulan: currentDate.getMonth() + 1,
    counter_id: user?.role === "user" ? user?.counter_id : "all",
  });

  // Tahun dari 2025 sampai 2035 (sama seperti di Input Nota)
  const tahun = Array.from({ length: 11 }, (_, i) => 2025 + i);
  const bulan = [
    { value: 1, label: "Januari" },
    { value: 2, label: "Februari" },
    { value: 3, label: "Maret" },
    { value: 4, label: "April" },
    { value: 5, label: "Mei" },
    { value: 6, label: "Juni" },
    { value: 7, label: "Juli" },
    { value: 8, label: "Agustus" },
    { value: 9, label: "September" },
    { value: 10, label: "Oktober" },
    { value: 11, label: "November" },
    { value: 12, label: "Desember" },
  ];

  useEffect(() => {
    fetchCounters();
  }, []);

  useEffect(() => {
    fetchLaporan();
  }, [filters]);

  const fetchCounters = async () => {
    try {
      const response = await axiosInstance.get(`${API}/counters`);
      setCounters(response.data);
    } catch (error) {
      toast.error("Gagal memuat counter");
    }
  };

  const fetchLaporan = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        tahun: filters.tahun,
        bulan: filters.bulan,
      });

      // User sales HARUS selalu filter by counter mereka
      if (user?.role === "user") {
        params.append("counter_id", user.counter_id);
      } else if (filters.counter_id && filters.counter_id !== "all") {
        // Admin bisa pilih counter atau all
        params.append("counter_id", filters.counter_id);
      }

      const response = await axiosInstance.get(`${API}/laporan/bulanan?${params}`);
      setLaporan(response.data);
    } catch (error) {
      toast.error("Gagal memuat laporan");
    } finally {
      setLoading(false);
    }
  };

  const [showCopyDialog, setShowCopyDialog] = useState(false);
  const [copyText, setCopyText] = useState("");

  const handleCopyToExcel = () => {
    const bulanNama = bulan.find(b => b.value === filters.bulan)?.label;
    
    // Create header
    let text = `DUMONT - Preferred Key\nRekapitulasi Penjualan - ${bulanNama} ${filters.tahun}\n\n`;
    
    // Add column headers - dengan TANGGAL NOTA dan NOMOR NOTA
    text += "NAMA COUNTER\tBARCODE\tTANGGAL NOTA\tNOMOR NOTA\tNAMA BARANG\tSATUAN\tHARGA SATUAN\tJUMLAH\tDISKON 1 (%)\tDISKON 2 (%)\tTOTAL NETTO\n";
    
    // Add data rows - format baru (detail per item per nota)
    laporan.forEach(item => {
      text += `${item.nama_counter}\t${item.barcode}\t${item.tanggal}\t${item.nomor_nota}\t${item.nama_barang}\t${item.satuan || 'PCS'}\t${item.harga_satuan}\t${item.jumlah}\t${item.diskon1}\t${item.diskon2}\t${item.total}\n`;
    });
    



    setCopyText(text);
    setShowCopyDialog(true);
  };

  const handleExport = async () => {
    setExporting(true);
    
    // Get month name (3 characters only)
    const bulanNama = bulan.find(b => b.value === filters.bulan)?.label.substring(0, 3);
    
    // Get counter name (max 10 characters)
    let counterNama = "Semua";
    if (filters.counter_id && filters.counter_id !== "all") {
      const counter = counters.find(c => c.id === filters.counter_id);
      if (counter) {
        counterNama = counter.nama_counter
          .substring(0, 10)
          .replace(/\s+/g, ''); // Remove spaces
      }
    }
    
    // Format: Rekap_[Counter]_[Bln]_[Tahun].xlsx
    const fileName = `Rekap_${counterNama}_${bulanNama}_${filters.tahun}.xlsx`;
    
    try {
      const params = new URLSearchParams({
        tahun: filters.tahun,
        bulan: filters.bulan,
      });

      if (filters.counter_id && filters.counter_id !== "all") {
        params.append("counter_id", filters.counter_id);
      }
      
      const response = await axiosInstance.get(`${API}/laporan/export?${params}`, {
        responseType: 'blob',
      });

      // Create blob
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      
      // Cleanup
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
      
      toast.success(
        <div>
          <p className="font-semibold">✅ Download dimulai!</p>
          <p className="text-sm mt-1">File: {fileName}</p>
          <p className="text-xs mt-2">Cek bagian bawah browser atau folder Downloads</p>
        </div>, 
        { duration: 5000 }
      );
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error("Gagal download. Gunakan tombol 'Copy untuk Excel' sebagai alternatif.");
    } finally {
      setExporting(false);
    }
  };

  const totalNetto = laporan.reduce((sum, item) => sum + item.total_netto, 0);
  const totalQty = laporan.reduce((sum, item) => sum + item.total_qty, 0);
  const totalTransaksi = laporan.reduce((sum, item) => sum + item.jumlah_transaksi, 0);

  const formatRupiah = (amount) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <Link to="/">
                <Button variant="ghost" size="sm" data-testid="back-button">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <img src="/logo-dumont.jpg" alt="Dumont" className="h-12 w-auto" />
              <div className="border-l border-gray-300 pl-3">
                <h1 className="text-2xl font-bold text-gray-800">Laporan Bulanan</h1>
                <p className="text-sm text-gray-600">Rekapitulasi penjualan per counter</p>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Filters */}
        <Card className="border-0 shadow-lg bg-white/80 backdrop-blur-sm mb-6">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg">Filter Laporan</CardTitle>
                <CardDescription>Pilih periode dan counter untuk melihat laporan</CardDescription>
              </div>
              {user?.role === "admin" && (
                <div className="flex gap-2">
                  <Button
                    onClick={handleCopyToExcel}
                    disabled={laporan.length === 0}
                    variant="outline"
                    className="border-green-500 text-green-600 hover:bg-green-50"
                    data-testid="copy-button"
                  >
                    <FileText className="w-4 h-4 mr-2" />
                    Copy untuk Excel
                  </Button>
                  <Button
                    onClick={handleExport}
                    disabled={exporting || laporan.length === 0}
                    data-testid="export-button"
                    className="bg-gradient-to-r from-green-500 to-teal-600 hover:from-green-600 hover:to-teal-700 text-white shadow-lg hover:shadow-xl transition-all"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {exporting ? "Mengunduh..." : "Download Excel"}
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Tahun</label>
                <Select
                  value={filters.tahun.toString()}
                  onValueChange={(value) => setFilters({ ...filters, tahun: parseInt(value) })}
                >
                  <SelectTrigger data-testid="tahun-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {tahun.map((year) => (
                      <SelectItem key={year} value={year.toString()}>
                        {year}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Bulan</label>
                <Select
                  value={filters.bulan.toString()}
                  onValueChange={(value) => setFilters({ ...filters, bulan: parseInt(value) })}
                >
                  <SelectTrigger data-testid="bulan-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {bulan.map((month) => (
                      <SelectItem key={month.value} value={month.value.toString()}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">Counter</label>
                {user?.role === "user" ? (
                  // User sales hanya lihat counter mereka sendiri (DISABLED)
                  <div className="border rounded-md px-3 py-2 bg-gray-100 text-gray-700">
                    {counters.find(c => c.id === user.counter_id)?.nama_counter || "Counter Anda"}
                  </div>
                ) : (
                  // Admin bisa pilih counter
                  <Select
                    value={filters.counter_id || undefined}
                    onValueChange={(value) => setFilters({ ...filters, counter_id: value })}
                  >
                    <SelectTrigger data-testid="counter-filter-select">
                      <SelectValue placeholder="Semua Counter" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Semua Counter</SelectItem>
                      {counters.map((counter) => (
                        <SelectItem key={counter.id} value={counter.id}>
                          {counter.nama_counter}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <Card className="border-0 shadow-lg bg-gradient-to-br from-green-500 to-teal-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-green-50">Total Penjualan Netto</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold" data-testid="total-netto">
                {formatRupiah(totalNetto)}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-blue-50">Total Jumlah Barang</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="total-qty">
                {totalQty}
              </div>
            </CardContent>
          </Card>

          <Card className="border-0 shadow-lg bg-gradient-to-br from-purple-500 to-pink-600 text-white">
            <CardHeader className="pb-3">
              <CardTitle className="text-sm font-medium text-purple-50">Total Transaksi</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold" data-testid="total-transaksi">
                {totalTransaksi}
              </div>
            </CardContent>
          </Card>
        </div>




        {/* Copy Dialog */}
        <Dialog open={showCopyDialog} onOpenChange={setShowCopyDialog}>
          <DialogContent className="max-w-4xl max-h-[80vh]">
            <DialogHeader>
              <DialogTitle className="text-xl">📋 Copy Data untuk Excel</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="font-semibold text-blue-900 mb-2">Cara Menggunakan:</p>
                <ol className="text-sm text-blue-800 space-y-1 list-decimal list-inside">
                  <li><strong>Klik tombol "Select All"</strong> di bawah (atau tekan Ctrl+A di kotak text)</li>
                  <li><strong>Copy</strong> dengan menekan <kbd className="px-2 py-1 bg-white border rounded">Ctrl+C</kbd></li>
                  <li><strong>Buka Excel/Google Sheets</strong></li>
                  <li><strong>Paste</strong> dengan <kbd className="px-2 py-1 bg-white border rounded">Ctrl+V</kbd> di cell A1</li>
                  <li>Data akan <strong>otomatis tersusun rapi</strong> dalam kolom!</li>
                </ol>
              </div>
              
              <div>
                <Label className="text-sm font-medium mb-2 block">Data Laporan (sudah siap untuk Excel):</Label>
                <textarea
                  id="copy-textarea"
                  className="w-full h-96 p-3 border rounded-lg font-mono text-xs"
                  value={copyText}
                  readOnly
                  onClick={(e) => e.target.select()}
                />
              </div>
              
              <div className="flex gap-2">
                <Button
                  onClick={() => {
                    const textarea = document.getElementById('copy-textarea');
                    textarea.select();
                    document.execCommand('copy');
                    toast.success("✅ Data sudah di-copy! Paste ke Excel dengan Ctrl+V", { duration: 5000 });
                  }}
                  className="flex-1 bg-green-600 hover:bg-green-700 text-white"
                >
                  ✂️ Select All & Copy
                </Button>
                <Button
                  onClick={() => setShowCopyDialog(false)}
                  variant="outline"
                >
                  Tutup
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>



      </main>
    </div>
  );
};

export default Laporan;
